const React = require('react');
const { useCallback, useEffect, useMemo, useRef, useState } = require('react');
const { useSelector } = require('react-redux');
const { useTranslation } = require('react-i18next');
const { useLocation } = require('react-router-dom');
const {
  buildSearchIndex,
  searchEntities,
  resolveRecentFieldEntries,
  buildResultAnalytics,
  countEntriesByType,
  getSearchNavigationPlan,
} = require('./globalSearchHelpers');
const GlobalSearchResults = require('./GlobalSearchResults');
const useGlobalSearchNavigation = require('./useGlobalSearchNavigation');
const { getRecentFieldIds } = require('./recentFieldsStore');
const { RESULT_SOURCE } = require('./GlobalSearch.types');

const searchIcon = require('images/icons/search_icon.svg');

const KEYS = {
  ARROW_DOWN: 'ArrowDown',
  ARROW_UP: 'ArrowUp',
  ENTER: 'Enter',
  ESCAPE: 'Escape',
};

const SELECTION_METHOD = { KEYBOARD: 'keyboard', MOUSE: 'mouse' };

const wrapIndex = (current, delta, length) =>
  length === 0 ? 0 : (current + delta + length) % length;

const GlobalSearchModal = ({ onClose }) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef(null);
  const rowRefs = useRef([]);
  const goToEntry = useGlobalSearchNavigation();

  const location = useLocation();
  const distributions = useSelector(
    (state) => state.distribution_data?.distributions,
  );

  // Built once per distributions refresh, and only while the modal is open —
  // the modal is unmounted when closed.
  const searchIndex = useMemo(
    () => buildSearchIndex(distributions),
    [distributions],
  );
  const { results, totalMatches } = useMemo(
    () => searchEntities(searchIndex, query),
    [searchIndex, query],
  );

  // Snapshotted on open rather than subscribed to: the modal is unmounted
  // while closed, so it is remounted — and re-reads — on every open, and no
  // field can be visited while it is up.
  const [recentFieldIds] = useState(getRecentFieldIds);
  const recentEntries = useMemo(
    () => resolveRecentFieldEntries(searchIndex, recentFieldIds),
    [searchIndex, recentFieldIds],
  );

  // One list drives rendering, the arrow keys and Enter, so the recents behave
  // exactly like results rather than being a second, half-wired mode.
  const hasQuery = query.trim().length > 0;
  const entries = hasQuery ? results : recentEntries;

  useEffect(() => {
    inputRef.current?.focus();
    // Tracked here rather than on the open flag so the event can carry how much
    // was searchable, and so it fires exactly once per open (the modal is
    // unmounted while closed).
    mixpanel.track('Global Search Opened', {
      source: 'keyboard_shortcut',
      origin_path: location.pathname,
      recent_fields: recentEntries.length,
      ...countEntriesByType(searchIndex),
    });
    // Mount only — searchIndex/location are read once, on open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    rowRefs.current = [];
    setActiveIndex(0);
  }, [query]);

  useEffect(() => {
    rowRefs.current[activeIndex]?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, entries]);

  // Escape closes even when focus has moved off the input.
  useEffect(() => {
    const handleDocumentKeyDown = (event) => {
      if (event.key === KEYS.ESCAPE) {
        event.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', handleDocumentKeyDown);
    return () => document.removeEventListener('keydown', handleDocumentKeyDown);
  }, [onClose]);

  const selectResult = useCallback(
    (index, selectionMethod) => {
      const entry = entries[index];
      if (!entry) {
        return;
      }
      mixpanel.track(
        'Global Search Result Selected',
        buildResultAnalytics(entry, {
          resultSource: hasQuery ? RESULT_SOURCE.SEARCH : RESULT_SOURCE.RECENT,
          selectionMethod,
          resultIndex: index,
          // totalMatches counts beyond the rendered cap; the recents are never
          // capped, so the list length is the real count there.
          resultCount: hasQuery ? totalMatches : entries.length,
          queryLength: query.trim().length,
          originPath: location.pathname,
          navigatedTo: getSearchNavigationPlan(entry, location.pathname).path,
        }),
      );
      goToEntry(entry);
      onClose();
    },
    [
      entries,
      goToEntry,
      hasQuery,
      location.pathname,
      onClose,
      query,
      totalMatches,
    ],
  );

  const handleInputKeyDown = useCallback(
    (event) => {
      if (event.key === KEYS.ARROW_DOWN) {
        event.preventDefault();
        setActiveIndex((current) => wrapIndex(current, 1, entries.length));
      } else if (event.key === KEYS.ARROW_UP) {
        event.preventDefault();
        setActiveIndex((current) => wrapIndex(current, -1, entries.length));
      } else if (event.key === KEYS.ENTER) {
        event.preventDefault();
        selectResult(activeIndex, SELECTION_METHOD.KEYBOARD);
      }
    },
    [activeIndex, entries.length, selectResult],
  );

  const handleOverlayMouseDown = useCallback(
    (event) => {
      if (event.target === event.currentTarget) {
        onClose();
      }
    },
    [onClose],
  );

  // Rows are only reachable by pointer; Enter goes through the input handler.
  const selectWithMouse = useCallback(
    (index) => selectResult(index, SELECTION_METHOD.MOUSE),
    [selectResult],
  );

  // Keep the caret in the input when a result is clicked.
  const keepInputFocused = useCallback((event) => event.preventDefault(), []);

  return (
    <div className='globalSearchOverlay' onMouseDown={handleOverlayMouseDown}>
      <div
        className='globalSearchPanel'
        role='dialog'
        aria-modal='true'
        aria-label={t('global_search_placeholder')}
      >
        <div className='globalSearchPanel__inputRow'>
          <img className='globalSearchPanel__icon' src={searchIcon} alt='' />
          <input
            ref={inputRef}
            className='globalSearchPanel__input'
            type='text'
            value={query}
            placeholder={t('global_search_placeholder')}
            aria-label={t('global_search_placeholder')}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={handleInputKeyDown}
          />
        </div>
        <div
          className='globalSearchPanel__results'
          role='listbox'
          onMouseDown={keepInputFocused}
        >
          <GlobalSearchResults
            entries={entries}
            totalMatches={totalMatches}
            hasQuery={hasQuery}
            activeIndex={activeIndex}
            onSelect={selectWithMouse}
            onHover={setActiveIndex}
            rowRefs={rowRefs}
          />
        </div>
        <div className='globalSearchPanel__footer'>
          <span>
            <kbd>↑</kbd>
            <kbd>↓</kbd> {t('global_search_hint_navigate')}
          </span>
          <span>
            <kbd>↵</kbd> {t('global_search_hint_select')}
          </span>
          <span>
            <kbd>esc</kbd> {t('close')}
          </span>
        </div>
      </div>
    </div>
  );
};

module.exports = GlobalSearchModal;
